import axios, { AxiosResponse } from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, snackbarSettingsAtom } from "../../hooks/useSnackbar";
import { useLogin } from "../login/login";
import {
    artistDashboardLoadingAtom,
    artistDashboardPayloadAtom,
    IArtistDashboardResponse
} from "./types";

/**
 * `GET /api/v2/artist-dashboard` — licensing earnings for the signed-in brand.
 *
 * `brandId` is deliberately OPTIONAL and only meaningful for admins: the
 * endpoint ignores it for everyone else and pins the query to the session's
 * brand. Non-admin callers must not send it (the legacy `/payout/*` endpoints
 * trust a client-supplied brand_id; this one does not, and we keep it that way).
 */

type UseArtistDashboard = [
    IArtistDashboardResponse | null,
    boolean,
    ArtistDashboardKeyFunctions
];

export type ArtistDashboardKeyFunctions = {
    get: (params?: IArtistDashboardQuery) => Promise<IArtistDashboardResponse | null>;
};

export interface IArtistDashboardQuery {
    startDate?: string | null;
    endDate?: string | null;
    /** Admin-only override. Omit for normal users. */
    brandId?: number | null;
}

export const useArtistDashboard = (): UseArtistDashboard => {

    const [payload, setPayload] = useAtom(artistDashboardPayloadAtom);
    const [loading, setLoading] = useAtom(artistDashboardLoadingAtom);

    const setSnackbar = useSetAtom(snackbarSettingsAtom);
    const [, authToken, loginFunctions] = useLogin();

    const get = async (params: IArtistDashboardQuery = {}): Promise<IArtistDashboardResponse | null> => {
        setLoading(true);

        const query = new URLSearchParams();
        if (params.startDate) query.append('startDate', params.startDate);
        if (params.endDate) query.append('endDate', params.endDate);
        if (params.brandId) query.append('brandId', String(params.brandId));

        const suffix = query.toString() ? `?${query.toString()}` : '';

        const results: AxiosResponse<IArtistDashboardResponse> | void = await axios.get(
            `${environment.artistDashboardUrl}${suffix}`,
            { headers: { 'X-Wallmates-Auth': authToken } }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        setLoading(false);

        if (results) {
            setPayload(results.data);
            return results.data;
        }

        setSnackbar({
            show: true,
            snackbarLevel: 'error',
            text: 'There was an error loading your licensing earnings. Please try again later.'
        });
        return null;
    };

    return [payload, loading, { get }];
};
